import React from 'react'
import classNames from 'classnames'

import { SkeletonLoader } from '@grammarly/design-system'

import styles from './AsyncImage.module.scss'

type Props = {
  readonly src: string
  readonly alt?: string
  readonly width?: number
  readonly height?: number
  readonly className?: string
  readonly onError?: (error: Error) => void
  readonly loadingState?: () => React.ReactNode
  readonly errorState?: () => React.ReactNode
  readonly isDecorative?: boolean
}

const defaultLoadingState = () => (
  <div role="progressbar" aria-label="Loading image" aria-valuetext="Loading image">
    <SkeletonLoader.Rectangle height="100%" width="100%" />
  </div>
)

const defaultErrorState = () => (
  <div className={styles.AsyncImage__error} role="img" aria-live="polite" />
)

export function AsyncImage({
  src,
  alt = '',
  width,
  height,
  className = '',
  onError,
  loadingState = defaultLoadingState,
  errorState = defaultErrorState,
  isDecorative = false,
}: Props) {
  const [isLoading, setIsLoading] = React.useState(true)
  const [hasError, setHasError] = React.useState(false)

  const handleLoad = () => {
    setIsLoading(false)
  }

  const handleError = (_error: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setIsLoading(false)
    setHasError(true)
    onError?.(new Error('Failed to load image'))
  }

  if (hasError) {
    return (
      <div
        className={classNames(styles.AsyncImage, className)}
        style={{ width, height }}
        aria-label={isDecorative ? '' : alt || 'Failed to load image'}
      >
        {errorState?.()}
      </div>
    )
  }

  return (
    <div className={classNames(styles.AsyncImage, className)} style={{ width, height }}>
      <img
        src={src}
        alt={isDecorative ? '' : alt}
        width={width}
        height={height}
        className={classNames(
          styles.AsyncImage__image,
          isLoading && styles.AsyncImage__image__loading,
        )}
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
        aria-label={isDecorative ? '' : alt}
        aria-hidden={isDecorative}
      />
      {isLoading && <div className={styles.AsyncImage__loader}>{loadingState?.()}</div>}
    </div>
  )
}
